import type { ReactElement } from 'react';
import type { ConversationItem } from '@chatapp-inbox/shared';
import {
  formatRemaining,
  replyWindowHoursOf,
  useLines,
  type CountdownPhase,
  type WindowRefusal,
} from '../conversation';
import { templatesAvailable, useTemplates } from '../templates';
import notice from './WriteNotice.module.css';
import styles from './ReplyWindowNotice.module.css';

export const WINDOW_CLOSED_TITLE = 'Janela de resposta encerrada';
export const DRAFT_KEPT_TEXT = 'O texto que você digitou ficou guardado como rascunho.';

/**
 * Por que não dá para responder. O prazo (N horas) é o do NÚMERO da conversa; sem ele (número fora
 * da lista, por exemplo desligado) a frase fica sem número em vez de chutar. A interface não
 * nomeia o provedor da regra: o texto fala do WhatsApp, que é o canal.
 */
export function windowClosedText(hours: number | null, canSendTemplate = false): string {
  const limit = hours === null ? 'o prazo permitido' : `${hours} ${hours === 1 ? 'hora' : 'horas'}`;
  return (
    `O WhatsApp só permite mensagem livre até ${limit} depois da última mensagem do cliente. ` +
    'Quando ele escrever de novo, você volta a poder responder aqui.' +
    (canSendTemplate ? ' Para retomar a conversa agora, envie um modelo de mensagem aprovado.' : '')
  );
}

/**
 * No lugar do compositor quando a janela de resposta fechou (mesmo padrão visual do `WriteNotice`,
 * que tem prioridade: sem permissão de escrever nunca aparecem os dois). Quem reabre a janela é o
 * cliente; enquanto isso, "Enviar modelo" (se o provedor tem modelos) retoma a conversa. O anúncio
 * para leitor de tela é da região `role="status"` do `ChatPane`, que já existia antes de este aviso aparecer.
 */
export function ReplyWindowNotice({
  conversation,
  refusal,
  onSendTemplate,
}: {
  conversation: Pick<ConversationItem, 'line'>;
  /** O servidor recusou um envio desta janela: a explicação dele fica à vista. */
  refusal: WindowRefusal | null;
  /** Abre a janela de envio de modelo. */
  onSendTemplate: () => void;
}): ReactElement {
  const lines = useLines();
  const templates = useTemplates();
  const canSendTemplate = templatesAvailable(templates.data);
  const hours = replyWindowHoursOf(lines.data, conversation.line?.id);
  return (
    <div className={notice.notice}>
      <div className={styles.body}>
        <h3 className={styles.title}>{WINDOW_CLOSED_TITLE}</h3>
        <p className={styles.text}>{windowClosedText(hours, canSendTemplate)}</p>
        {refusal && (
          <p className={styles.refusal}>
            {refusal.message}
            {refusal.draftKept ? ` ${DRAFT_KEPT_TEXT}` : ''}
          </p>
        )}
      </div>
      {canSendTemplate && (
        <button type="button" className={styles.action} onClick={onSendTemplate}>
          Enviar modelo
        </button>
      )}
    </div>
  );
}

/**
 * Linha pequena acima do compositor quando faltam 2 h ou menos. Texto simples (NÃO é região viva:
 * mudaria a cada 30 s); os anúncios para leitor de tela são só os dois do `ChatPane`. Nos últimos
 * 10 min ganha peso, além da cor, para o destaque não depender dela.
 */
export function ReplyWindowCountdown({
  remainingMs,
  phase,
}: {
  remainingMs: number;
  phase: CountdownPhase;
}): ReactElement {
  return (
    <p className={`${styles.countdown} ${phase === 'urgent' ? styles.urgent : ''}`}>
      {`A janela de resposta fecha em ${formatRemaining(remainingMs)}`}
    </p>
  );
}
